/**
 * Locations screen (DNKADM-0051 U1) — the owned `v2_location` list + create/edit/
 * deactivate/reactivate (handoff §5.2). `RequireAdmin`-gated route + a Configuration
 * nav item. The owned location id this screen manages is the scope the whole v2 admin
 * surface keys by (§4.6).
 *
 * Loads `listLocations({ status })` through `useResource`, keyed on the
 * Active/Inactive/All filter (`useActiveFilter` + `<ActiveFilter>`, §4.3). A table of
 * name → timezone → status; the row/name/chevron click opens the detail (tax rates,
 * U3) per the DNKADM-0284 row-open pattern, with Deactivate/Reactivate as explicit
 * secondary actions that stop propagation. Base-field editing lives on the detail
 * page (DNKADM-0286 retired the list quick-edit trigger); "Add location" opens the
 * {@link LocationDialog} to create. Every write refetches + toasts
 * (refetch-after-write). A first-run empty tenant (§8) shows a strong empty state
 * with a create CTA.
 *
 * **Toolbar, sort and URL (DNKADM-0430 P1-F).** The shared {@link ListToolbar} holds a
 * name/timezone search and the Active/Inactive/All filter; every data column has shared
 * {@link SortableHeader}s (default Name ascending). Search, filter and sort live in the
 * URL (`locationsListView.ts`).
 */
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { useListUrlState } from '@/components/list/useListUrlState';
import { Icon, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useUrlActiveFilter } from '@/hooks/useActiveFilter';
import { ActiveFilter } from '@/components/ActiveFilter';
import { useToast } from '@/components/useToast';
import { serverMessage } from '@/api/serverMessage';
import {
  deactivateLocation,
  listLocations,
  reactivateLocation,
  type V2Location,
} from '@/api/locations';
import { getPlanUsage, planLimitFor } from '@/api/planUsage';
import { PlanLimitDialog } from '@/components/PlanLimitDialog';
import { parsePlanLimit, v2ErrorCode, type PlanLimitInfo } from '@/api/v2Errors';
import { LocationDialog } from './LocationDialog';
import {
  LOCATION_DEFAULT_SORT,
  LOCATION_SORT_KEYS,
  LOCATIONS_URL_DEFAULTS,
  locationMatches,
  locationName,
  locationSortValue,
  type LocationSortKey,
} from './locationsListView';
import { ScreenError } from '@/components/ScreenError';

export function LocationsScreen() {
  const t = resolveTokens(useTheme());
  const showToast = useToast();
  const navigate = useNavigate();
  // DNKADM-0430: search, status filter and sort come from the URL (`locationsListView.ts`).
  const url = useListUrlState(LOCATIONS_URL_DEFAULTS);
  const setUrl = url.set;
  const filter = useUrlActiveFilter(url.values.status, setUrl);
  const search = url.values.q;
  const sort = parseSort(url.values.sort, url.values.dir, LOCATION_SORT_KEYS, LOCATION_DEFAULT_SORT);

  const locations = useResource(
    useCallback(() => listLocations({ status: filter.status }), [filter.status]),
    [filter.status],
  );

  // Plan usage drives the button-level Add gate (DNKADM-0069); loaded once and
  // refetched after each add/delete (a slot changes). Degrades to the submit-time
  // gate (DNKADM-0065) when it can't load — `planLimitFor` returns null on error.
  const planUsage = useResource(getPlanUsage);

  // Dialog state: `null` = closed; `{ location: null }` = add; `{ location }` = edit.
  const [dialog, setDialog] = useState<{ location: V2Location | null } | null>(null);
  // The plan-limit popup payload when Add is gated at the button; null = closed.
  const [planLimit, setPlanLimit] = useState<PlanLimitInfo | null>(null);

  const refetch = locations.refetch;

  const visibleRows = useMemo(
    () =>
      sortRows(
        (locations.data ?? []).filter((row) => locationMatches(row, search)),
        (row) => locationSortValue(row, sort.key),
        sort.dir,
        (row) => row.id,
      ),
    [locations.data, search, sort.key, sort.dir],
  );
  const onSort = useCallback(
    (key: LocationSortKey) => setUrl(toSortParams(toggleSort(sort, key), LOCATION_DEFAULT_SORT)),
    [setUrl, sort],
  );
  const refetchUsage = planUsage.refetch;

  // Add: when the axis is at its plan cap, open the upgrade popup directly (the
  // form never opens); otherwise open the create form exactly as before. An
  // unknown/unlimited/unavailable usage never gates (`planLimitFor` → null).
  const handleAdd = useCallback(() => {
    const info = planLimitFor(planUsage.data, 'locations');
    if (info) {
      setPlanLimit(info);
      return;
    }
    setDialog({ location: null });
  }, [planUsage.data]);

  const handleDeactivate = useCallback(
    async (location: V2Location) => {
      try {
        await deactivateLocation(location.id);
        refetch();
        refetchUsage();
        showToast('Location deactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t deactivate the location.', 'error');
      }
    },
    [refetch, refetchUsage, showToast],
  );

  const handleReactivate = useCallback(
    async (location: V2Location) => {
      try {
        await reactivateLocation(location.id);
        refetch();
        refetchUsage();
        showToast('Location reactivated', 'success');
      } catch (err) {
        // Reactivate consumes a plan slot (counts are of ACTIVE records), so it is
        // plan-limit enforced exactly like Add (DNKADM-0065 / DNKAPI-0139) — mirror
        // the create flow's branching, keyed on the body `error` code (not status).
        const code = v2ErrorCode(err);
        if (code === 'PLAN_LIMIT_REACHED') {
          // The cap was hit — open the upgrade popup (NOT a generic toast); the
          // record was not reactivated.
          setPlanLimit(parsePlanLimit(err));
          return;
        }
        if (code === 'PLAN_VERIFY_FAILED') {
          // Transient plan-service outage — a retry toast (NOT the popup).
          showToast(
            'We couldn’t verify your plan just now. Please try again in a moment.',
            'error',
          );
          return;
        }
        showToast(serverMessage(err) ?? 'Couldn’t reactivate the location.', 'error');
      }
    },
    [refetch, refetchUsage, showToast],
  );

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" component="h1">
            Locations
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            The tenant’s locations. Each owns its timezone, weekly hours, and tax rates.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Icon name="add" />}
          onClick={handleAdd}
          sx={{ flexShrink: 0 }}
        >
          Add location
        </Button>
      </Stack>

      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next }),
          placeholder: 'Search locations',
          disabled: locations.status !== 'ready',
        }}
        filters={
          <ActiveFilter
            value={filter.value}
            onChange={filter.setValue}
            aria-label="Filter locations by status"
          />
        }
        onClear={() => setUrl({ q: null, status: null })}
        canClear={search.length > 0 || filter.value !== 'active'}
      />

      {locations.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={220}
          role="status"
          aria-label="Loading locations"
          sx={{ borderRadius: 3 }}
        />
      )}

      {locations.status === 'error' && (
        <ScreenError
          error={locations.error}
          what="the locations"
          onRetry={locations.refetch}
          sx={{ alignItems: 'center' }}
        />
      )}

      {locations.status === 'ready' && locations.data && locations.data.length === 0 && (
        // First-run empty state (§8) with a strong create CTA.
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: t.lineStrong,
            borderRadius: 3,
          }}
        >
          <Icon name="apartment" sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography sx={{ mt: 1, fontWeight: 600 }}>
            {filter.value === 'active' ? 'No locations yet' : 'No locations match this filter'}
          </Typography>
          {filter.value === 'active' && (
            <>
              <Typography variant="body2" sx={{ mt: 0.5, mb: 2.5, color: 'text.secondary' }}>
                Add your first location to set its hours and tax rates and start taking bookings.
              </Typography>
              <Button variant="contained" startIcon={<Icon name="add" />} onClick={handleAdd}>
                Add location
              </Button>
            </>
          )}
        </Paper>
      )}

      {locations.status === 'ready' && locations.data && locations.data.length > 0 && (
        <TableContainer>
          <Table aria-label="Locations" size="small">
            <TableHead>
              <TableRow>
                <SortableHeader label="Name" col="name" sort={sort} onSort={onSort} />
                <SortableHeader label="Timezone" col="timezone" sort={sort} onSort={onSort} />
                <SortableHeader label="Status" col="status" sort={sort} onSort={onSort} />
                <TableCell align="right">Actions</TableCell>
                <TableCell sx={{ width: 44 }} aria-hidden />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((location) => (
                <TableRow
                  key={location.id}
                  hover
                  onClick={() => navigate(`/locations/${location.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <MuiLink
                      component={RouterLink}
                      to={`/locations/${location.id}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {locationName(location)}
                    </MuiLink>
                  </TableCell>
                  <TableCell>{location.timezone === '' ? '—' : location.timezone}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={location.isActive ? 'Active' : 'Inactive'}
                      color={location.isActive ? 'success' : 'default'}
                      variant={location.isActive ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {location.isActive ? (
                      <IconAction
                        size="small"
                        label={`Deactivate location ${location.name || location.id}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleDeactivate(location);
                        }}
                      >
                        <Icon name="toggle_off" />
                      </IconAction>
                    ) : (
                      <IconAction
                        size="small"
                        label={`Reactivate location ${location.name || location.id}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleReactivate(location);
                        }}
                      >
                        <Icon name="toggle_on" />
                      </IconAction>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ color: t.textGhost }}>
                    <IconAction
                      size="small"
                      label={`Open ${location.name || location.id}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/locations/${location.id}`);
                      }}
                    >
                      <Icon name="chevron_right" />
                    </IconAction>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {locations.status === 'ready' &&
        locations.data &&
        locations.data.length > 0 &&
        visibleRows.length === 0 && <ListNoMatch noun="locations" query={search} />}

      <LocationDialog
        open={dialog !== null}
        location={dialog?.location ?? null}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refetch();
          refetchUsage();
        }}
        showToast={showToast}
      />

      <PlanLimitDialog
        open={planLimit !== null}
        info={planLimit}
        onClose={() => setPlanLimit(null)}
      />
    </Box>
  );
}
